/**
 * Salvage part slots, the wreck-frame → world transform of attached parts and anchors, and the dynamic bodies of
 * freed parts (M2-17, plan §6 M2-17).
 *
 * An attached part has no body: its world position, rotation and anchors follow its wreck every tick
 * (`placeAttachedParts`). When its last anchor is cut it becomes a dynamic body (`createPartBody`): the prop's hull
 * collider (a ball of the part radius until the prop model has loaded), `DEBRIS_COLLISION_GROUPS`, `EID_DEBRIS`,
 * the part's mass in tonnes. Per-tick functions are allocation-free (D-014); bodies are built and removed on rare
 * events. Positions are float64 world metres, rebased only at the physics origin (D-011, D-013).
 */
import RAPIER from '@dimforge/rapier3d-compat'
import type { RigidBody } from '@dimforge/rapier3d-compat'
import type { ColliderData } from '../assets/extract'
import type { Vec3 } from '../core/math'
import { MAX_ANCHORS_PER_PART } from '../data/m2Schemas'
import { DEBRIS_COLLISION_GROUPS, EID_DEBRIS, type Physics } from '../physics/physics'
import { wreckToWorld } from '../salvage/layout'
import type { PartPool, WreckPool } from './contracts'

/** Kilograms per tonne (Rapier masses are kilograms, like the ship defs). */
export const KG_PER_TONNE = 1000
/** Solid-sphere inertia factor (2/5 m r²) for a freed part. */
const SPHERE_INERTIA = 0.4

/** Takes a free part slot (lowest first), marks it alive and returns it; -1 when the pool is full. */
export function allocPartSlot(pool: PartPool): number {
  for (let p = 0; p < pool.capacity; p++) {
    if (pool.alive[p] !== 0) continue
    pool.alive[p] = 1
    pool.live++
    if (p + 1 > pool.high) pool.high = p + 1
    return p
  }
  return -1
}

/** Frees part slot `p` (clears its flags and contents) and shrinks `high` past trailing dead slots. */
export function releasePartSlot(pool: PartPool, p: number): void {
  if (pool.alive[p] === 0) return
  pool.alive[p] = 0
  pool.live--
  pool.freed[p] = 0
  pool.secured[p] = 0
  pool.tractored[p] = 0
  pool.contents[p] = null
  while (pool.high > 0 && pool.alive[pool.high - 1] === 0) pool.high--
}

const wp: Vec3 = { x: 0, y: 0, z: 0 }

/**
 * Places every attached (not freed) part and its anchors at their wreck's pose: world position, the wreck's
 * rotation and velocity. Allocation-free.
 */
export function placeAttachedParts(wrecks: WreckPool, parts: PartPool): void {
  for (let p = 0; p < parts.high; p++) {
    if (parts.alive[p] === 0 || parts.freed[p] !== 0) continue
    const w = parts.wreck[p]
    const ox = wrecks.x[w]
    const oy = wrecks.y[w]
    const oz = wrecks.z[w]
    const qx = wrecks.qx[w]
    const qy = wrecks.qy[w]
    const qz = wrecks.qz[w]
    const qw = wrecks.qw[w]
    wreckToWorld(ox, oy, oz, qx, qy, qz, qw, parts.lx[p], parts.ly[p], parts.lz[p], wp)
    parts.x[p] = wp.x
    parts.y[p] = wp.y
    parts.z[p] = wp.z
    parts.qx[p] = qx
    parts.qy[p] = qy
    parts.qz[p] = qz
    parts.qw[p] = qw
    parts.vx[p] = wrecks.vx[w]
    parts.vy[p] = wrecks.vy[w]
    parts.vz[p] = wrecks.vz[w]
    const base = p * MAX_ANCHORS_PER_PART
    const n = parts.anchorCount[p]
    for (let a = 0; a < n; a++) {
      const i = base + a
      wreckToWorld(ox, oy, oz, qx, qy, qz, qw, parts.alx[i], parts.aly[i], parts.alz[i], wp)
      parts.ax[i] = wp.x
      parts.ay[i] = wp.y
      parts.az[i] = wp.z
    }
  }
}

/**
 * A dynamic body for freed part `p` at its current world pose and velocity (the caller sets both first):
 * one collider per convex hull of `collider` (a ball of `radius` otherwise), zero density with the mass set from
 * `massT`, mapped to `EID_DEBRIS`. A rare event (D-024).
 */
export function createPartBody(p: Physics, parts: PartPool, slot: number, collider: ColliderData | null, radius: number): RigidBody {
  const mass = parts.massT[slot] * KG_PER_TONNE
  const inertia = SPHERE_INERTIA * mass * radius * radius
  const desc = RAPIER.RigidBodyDesc.dynamic()
    .setTranslation(parts.x[slot] - p.origin.x, parts.y[slot] - p.origin.y, parts.z[slot] - p.origin.z)
    .setRotation({ x: parts.qx[slot], y: parts.qy[slot], z: parts.qz[slot], w: parts.qw[slot] })
    .setLinvel(parts.vx[slot], parts.vy[slot], parts.vz[slot])
    .setAdditionalMassProperties(mass, { x: 0, y: 0, z: 0 }, { x: inertia, y: inertia, z: inertia }, { x: 0, y: 0, z: 0, w: 1 })
  const body = p.world.createRigidBody(desc)
  let built = 0
  if (collider?.kind === 'convexHulls') {
    for (let i = 0; i < collider.hulls.length; i++) {
      const shape = RAPIER.ColliderDesc.convexHull(collider.hulls[i])
      if (!shape) continue
      try {
        const handle = p.world.createCollider(shape.setDensity(0).setCollisionGroups(DEBRIS_COLLISION_GROUPS), body).handle
        p.colliderEid.set(handle, EID_DEBRIS)
        built++
      } catch {
        // A degenerate hull: fall back to the ball below when none is left.
      }
    }
  }
  if (built === 0) {
    const r = collider?.kind === 'ball' ? collider.radius : radius
    const handle = p.world.createCollider(RAPIER.ColliderDesc.ball(r).setDensity(0).setCollisionGroups(DEBRIS_COLLISION_GROUPS), body).handle
    p.colliderEid.set(handle, EID_DEBRIS)
  }
  body.recomputeMassPropertiesFromColliders()
  return body
}

/** Removes a part body, its colliders and their eid mappings. */
export function removePartBody(p: Physics, body: RigidBody): void {
  const n = body.numColliders()
  for (let i = 0; i < n; i++) p.colliderEid.delete(body.collider(i).handle)
  p.world.removeRigidBody(body)
}

const tv = { x: 0, y: 0, z: 0 }
const tq = { x: 0, y: 0, z: 0, w: 1 }

/** Per tick after the Rapier step: reads every freed part body into float64 world pose and velocity. Allocation-free. */
export function syncPartBodies(p: Physics, parts: PartPool, bodies: (RigidBody | undefined)[]): void {
  for (let s = 0; s < parts.high; s++) {
    if (parts.alive[s] === 0 || parts.freed[s] === 0) continue
    const body = bodies[s]
    if (body === undefined) continue
    body.translation(tv)
    parts.x[s] = p.origin.x + tv.x
    parts.y[s] = p.origin.y + tv.y
    parts.z[s] = p.origin.z + tv.z
    body.rotation(tq)
    parts.qx[s] = tq.x
    parts.qy[s] = tq.y
    parts.qz[s] = tq.z
    parts.qw[s] = tq.w
    body.linvel(tv)
    parts.vx[s] = tv.x
    parts.vy[s] = tv.y
    parts.vz[s] = tv.z
  }
}

/** Adds a velocity change (m/s) to a part body before the Rapier step (the tractor). Allocation-free. */
export function addPartVelocity(body: RigidBody, dvx: number, dvy: number, dvz: number): void {
  body.linvel(tv)
  tv.x += dvx
  tv.y += dvy
  tv.z += dvz
  body.setLinvel(tv, true)
}
